import {
  MAP_OPTIONS_PER_REGION,
  MAP_OPTIONS_PER_REGION_MOBILE,
  BOUNDS_PER_REGION,
  DEFAULT_THRESHOLD,
} from '../../consts';
import {
  DefaultRenderer,
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '/../assets/node_modules/@googlemaps/markerclusterer';
import htmx from 'htmx.org';
import { language } from '../../utils/language';

const MAP_CARD_ENDPOINT = 'map_card';

class CatalogueMapView {
  activeMarker = null;
  mobileMap = null;
  desktopMap = null;
  mobileMapMarkers = [];
  desktopMapMarkers = [];
  activeCard = null;
  currentZoomLevel = 15;
  markersArray = [];
  currentLat = null;
  currentLng = null;
  firstLoad = true;
  shouldReload = true;
  isHighlightActive = true;
  bounds = null;
  cluster = null;
  boundsPadding = 0;
  minPoints = 5; // cluster configuration. min number of markers a cluster can group
  radius = 60; // cluster configuration. radius of the cluster
  isMobile = false;
  mobilePlaceholderHtml = null;
  markerSvg =
    'data:image/svg+xml;utf-8, <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" height="43" width="43"> <circle cx="21.5" cy="21.5" r="20" fill="white" stroke="%23BDBDBD" stroke-width="0.7"/></svg>';

  DEFAULT_MAP_OPTIONS = {
    zoom: this.currentZoomLevel,
    threshold: DEFAULT_THRESHOLD,
    rotateControl: true,
    fullscreenControl: false,
    center: {
      lat: 53.342927,
      lng: -6.267683,
    },
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };

  MAP_OPTIONS = {};
  MAP_OPTIONS_MOBILE = {};

  constructor({ mapOptions, mobileMapOptions, defaultBounds }) {
    this.infoWindow = new google.maps.InfoWindow();

    this.activeMarkerIconUrl = $('#active-marker-svg-url').val();
    this.inactiveMarkerIconUrl = $('#inactive-marker-svg-url').val();
    this.MAP_OPTIONS = {
      ...this.DEFAULT_MAP_OPTIONS,
      ...mapOptions,
    };

    this.MAP_OPTIONS_MOBILE = {
      ...this.DEFAULT_MAP_OPTIONS,
      ...mobileMapOptions,
      controlSize: 30,
    };
    this.defaultBounds = defaultBounds;
  }

  deactivateAllMarkers = () => {
    if (this.activeMarker !== null) {
      this.activeMarker.setIcon({
        url: this.inactiveMarkerIconUrl,
        scaledSize: new google.maps.Size(43, 43),
      });
    }
    this.mobileMapMarkers.map((marker) =>
      marker.setIcon({
        url: this.inactiveMarkerIconUrl,
        scaledSize: new google.maps.Size(43, 43),
      })
    );
  };

  closeActiveInfoWindow = () => {
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow.setContent(null);
    }
  };

  deactivateSelectedProperty = () => {
    this.closeActiveInfoWindow();
  };

  storeMobilePlaceholderHtml = () => {
    if (this.mobilePlaceholderHtml === null) {
      const placeholder = $('#selected-property-placeholder');
      if (placeholder.length) {
        this.mobilePlaceholderHtml = placeholder.html();
      }
    }
  };

  resetMobilePlaceholder = () => {
    if (!this.isMobile) {
      return;
    }
    const placeholder = $('#selected-property-placeholder');
    if (!placeholder.length) {
      return;
    }
    if (this.mobilePlaceholderHtml !== null) {
      placeholder.html(this.mobilePlaceholderHtml);
    } else {
      placeholder.empty();
    }
  };

  resetMobileCard = () => {
    if (!this.isMobile) {
      return;
    }
    this.resetMobilePlaceholder();
    this.deactivateAllMarkers();
    this.activeMarker = null;
  };

  bindMobileCardInteractions = () => {
    if (!this.isMobile) {
      return;
    }
    this.storeMobilePlaceholderHtml();
    $(document)
      .off('click.mobileCard', '.mobile-map-card__close .close-button')
      .on(
        'click.mobileCard',
        '.mobile-map-card__close .close-button',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.resetMobileCard();
        }
      );
  };

  onPropertyHoverIn = (latlng) => {
    if (this.activeMarker !== null) {
      this.deactivateAllMarkers();
    }
    this.unhighlightCard();
    this.activeMarker = new google.maps.Marker({
      position: latlng,
      map: this.desktopMap,
      icon: {
        url: this.activeMarkerIconUrl,
        scaledSize: new google.maps.Size(43, 43),
      },
      optimized: false,
      zIndex:
        Number(google.maps.Marker.MAX_ZINDEX) + this.markersArray.length + 1,
    });
  };

  addHoveringBehaviorToHighlightMarker = (selector, marker) => {
    let thisMap = this;
    $(document).off('mouseenter', selector);
    $(document).on('mouseenter', selector, function (e) {
      if (thisMap.activeMarker !== null) {
        thisMap.deactivateAllMarkers();
      }
      marker.setIcon({
        url: thisMap.activeMarkerIconUrl,
        scaledSize: new google.maps.Size(43, 43),
      });
      thisMap.activeMarker = marker;
    });

    $(document).off('mouseleave', selector);
    $(document).on('mouseleave', selector, function (e) {
      if (!thisMap.isHighlightActive) {
        marker.setIcon({
          url: thisMap.inactiveMarkerIconUrl,
          scaledSize: new google.maps.Size(43, 43),
        });
      }
    });
  };

  addHoveringBehaviorToMarker = (selector, lat, lng) => {
    let thisMap = this;
    $(document).off('mouseenter', selector);
    $(document).on('mouseenter', selector, function (e) {
      thisMap.onPropertyHoverIn({ lat, lng });
    });

    $(document).off('mouseleave', selector);
    $(document).on('mouseleave', selector, function (e) {
      thisMap.activeMarker.setMap(null);
    });
  };

  clearOverlays = () => {
    if (this.markersArray.length > 0) {
      this.markersArray.map((a) => a.setMap(null));
      this.cluster.clearMarkers();
      this.markersArray = [];
    }
  };

  renderCountryMarkers = (pins) => {
    let thisMap = this;
    pins.forEach(function (pin) {
      const region = pin[0];
      const lat = MAP_OPTIONS_PER_REGION[region].center.lat;
      const lng = MAP_OPTIONS_PER_REGION[region].center.lng;
      const count = pin[1];

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map: thisMap.desktopMap,
        icon: {
          url: thisMap.markerSvg,
        },
        optimized: false,
        label: {
          text: count.toString(),
          fontWeight: 'bold',
          fontSize: '20px',
        },
      });

      thisMap.markersArray.push(marker);

      marker.addListener('click', () => {
        // mimic cluster behavior - 'zoom in' on click
        const countryBounds = BOUNDS_PER_REGION[region];
        const ne = new google.maps.LatLng(
          countryBounds.nelat,
          countryBounds.nelng
        );
        const sw = new google.maps.LatLng(
          countryBounds.swlat,
          countryBounds.swlng
        );
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(ne);
        bounds.extend(sw);
        thisMap.desktopMap.fitBounds(bounds, thisMap.boundsPadding);
        thisMap.config = thisMap.isMobile
          ? MAP_OPTIONS_PER_REGION_MOBILE[region]
          : MAP_OPTIONS_PER_REGION[region];
      });
    });
  };

  unhighlightCard = () => {
    this.isHighlightActive = false;
    $('.card-container.highlighted-container').css('border', 'none');
  };

  highlightCard = () => {
    this.isHighlightActive = true;
    $('.card-container.highlighted-container').css('display', 'block');
    $('.card-container.highlighted-container').css(
      'border',
      '1px solid #3A8DFB'
    );
  };

  // set pins on map according to their coordinates
  renderNormalMarkers = (pins) => {
    let thisMap = this;
    this.bounds = new google.maps.LatLngBounds();
    pins.forEach(function (pin) {
      const lat = pin[0];
      const lng = pin[1];
      const entityId = pin[2];
      const cardSelector = '#property-ad-' + entityId;

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map: thisMap.desktopMap,
        icon: {
          url: thisMap.inactiveMarkerIconUrl,
          scaledSize: new google.maps.Size(43, 43),
        },
        optimized: false,
      });
      const target = thisMap.isMobile
        ? '#selected-property-placeholder'
        : '#selected-map-property';
      marker.addListener('click', () => {
        htmx
          .ajax('GET', `/${language}/${MAP_CARD_ENDPOINT}/${entityId}`, {
            target: target,
            swap: 'innerHTML',
            values: { ismobile: thisMap.isMobile },
          })
          .then(() => {
            thisMap.highlightCard();
          });
        window.scrollTo(0, 0);
        thisMap.deactivateAllMarkers();
        thisMap.activeMarker = marker;
        marker.setIcon({
          url: thisMap.activeMarkerIconUrl,
          scaledSize: new google.maps.Size(43, 43),
        });
        thisMap.addHoveringBehaviorToHighlightMarker(
          '#selected-map-property',
          marker
        );
      });

      thisMap.addHoveringBehaviorToMarker(cardSelector, lat, lng);

      thisMap.markersArray.push(marker);
      thisMap.bounds.extend(marker.getPosition());
    });

    const renderer = {
      render: ({ count, position }) =>
        new google.maps.Marker({
          label: {
            text: String(count),
            color: 'black',
            fontSize: '20px',
            fontWeight: 'bold',
          },
          position,
          icon: {
            url: thisMap.markerSvg,
            scaledSize: new google.maps.Size(43, 43),
          },
          optimized: false,
        }),
    };

    const map = this.desktopMap;
    const markers = this.markersArray;

    // only fit to bounds if not in a country page
    if (this.firstLoad && Object.keys(this.defaultBounds).length === 0) {
      if (pins.length === 0) {
        // if no pins, set the zoom to see full map
        map.setZoom(2);
      } else {
        // if pins, fit to pins bounds
        map.fitBounds(this.bounds, this.boundsPadding);
      }
      this.shouldReload = false;
    }
    this.firstLoad = false;
    // Full option list can be found at: https://github.com/mapbox/supercluster#options
    const algorithm = new SuperClusterAlgorithm({
      minPoints: this.minPoints,
      radius: this.radius,
    });
    this.cluster = new MarkerClusterer({ map, markers, renderer, algorithm });
  };

  addLoadingState = () => {
    if (this.isMobile) {
      $('#loading-img-mobile').css('display', 'block');
    } else {
      $('#left-list').css('visibility', 'hidden');
      $('#loading-img').css('display', 'block');
    }
  };

  removeLoadingState = () => {
    if (this.isMobile) {
      $('#loading-img-mobile').css('display', 'none');
    } else {
      $('#left-list').css('visibility', 'initial');
      $('#loading-img').css('display', 'none');
    }
  };

  pageReload = (href) => {
    this.addLoadingState();
    htmx
      .ajax('GET', href, {
        target: '#dynamic-property-list',
        swap: 'innerHTML',
      })
      .then(() => {
        window.scrollTo(0, 0);
        this.removeLoadingState();
      });
  };

  mobileCheck = () => {
    let check = false;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  };

  create = () => {
    this.isMobile = this.mobileCheck();
    if (this.isMobile) {
      this.minPoints = 4;
      this.radius = 100;
    }
    this.bindMobileCardInteractions();
    const thisMap = this;
    $(document).on('click', '.page-item, .page-link', function (e) {
      // this is being used to change default pagination behavior (we dont reload the entire page anymore)
      const href = $(this).attr('href');
      if (href !== undefined) {
        e.preventDefault();
        thisMap.pageReload(href);
      }
    });

    // For mobile map default zoom value needs to be reduced by one unit to fit mobile screens
    if (this.isMobile) {
      this.config = this.MAP_OPTIONS_MOBILE;
      this.desktopMap = new google.maps.Map(
        $('#map-mobile')[0],
        this.MAP_OPTIONS_MOBILE
      );
    } else {
      this.config = this.MAP_OPTIONS;
      this.desktopMap = new google.maps.Map(
        $('#map-desktop')[0],
        this.MAP_OPTIONS
      );
    }

    this.path = window.location.pathname + window.location.search;

    // set map initial boundaries when in a country page
    if (Object.keys(defaultBounds).length !== 0 && !isFilterApplied()) {
      let ne = new google.maps.LatLng(defaultBounds.nelat, defaultBounds.nelng);
      let sw = new google.maps.LatLng(defaultBounds.swlat, defaultBounds.swlng);
      var bounds = new google.maps.LatLngBounds();
      bounds.extend(ne);
      bounds.extend(sw);
      this.desktopMap.fitBounds(bounds, this.boundsPadding);
    }

    this.desktopMap.addListener('click', () => {
      if (!!this.infoWindow.content) {
        this.deactivateAllMarkers();
        this.deactivateSelectedProperty();
      }
      this.resetMobileCard();
    });

    this.desktopMap.addListener('idle', () => {
      $('#selected-map-property').css('display', 'none');
      const newZoomLevel = this.desktopMap.getZoom();
      let center = this.desktopMap.getCenter();
      let newLat = center.lat();
      let newLng = center.lng();
      if (
        newLat === this.currentLat &&
        newLng == this.currentLng &&
        newZoomLevel == this.currentZoomLevel
      ) {
        // assures that we dont perform another request when centering the map on the selected property
      } else if (this.shouldReload === false) {
        // means that we auto moved the map to fit boundaries and therefore we dont need to redo search
        this.shouldReload = true; // next map change should trigger a new search
      } else {
        this.bounds = this.desktopMap.getBounds();
        let southWest = this.bounds.getSouthWest();
        let northEast = this.bounds.getNorthEast();
        let southWestLat = southWest.lat();
        let southWestLng = southWest.lng();
        let northEastLat = northEast.lat();
        let northEastLng = northEast.lng();

        this.clearOverlays();
        this.addLoadingState();
        const group_by_region =
          newZoomLevel > this.config.threshold ? false : true;
        const newDynamicList = htmx.ajax('GET', this.path, {
          target: '#dynamic-property-list',
          swap: 'innerHTML',
          values: {
            swlat: southWestLat,
            swlng: southWestLng,
            nelat: northEastLat,
            nelng: northEastLng,
            group_by_region: group_by_region,
            ismapsearch: true,
            firstload: this.firstLoad,
          },
        });
        if (newDynamicList === undefined) {
          // If the user moves the map too fast, the previous will be aborted and the request will be undefined so we can skip the pins loading
          return;
        }
        newDynamicList.then(() =>
          $.ajax(`/${language}/search/pins${window.location.search}`, {
            data: {
              swlat: southWestLat,
              swlng: southWestLng,
              nelat: northEastLat,
              nelng: northEastLng,
              group_by_region: group_by_region,
              ismapsearch: true,
              firstload: this.firstLoad,
            },
          }).done((result) => {
            if (result.pins.length > 0) {
              if (result.pin_type === 'country_pin') {
                this.renderCountryMarkers(result.pins);
              } else {
                this.renderNormalMarkers(result.pins);
              }
            }
            setTimeout(this.removeLoadingState, 2000);
          })
        );
      }
      this.currentLat = newLat;
      this.currentLng = newLng;
      this.currentZoomLevel = newZoomLevel;
    });
  };
}

const region = $('#static_country').val();

let mapOptions = {},
  mobileMapOptions = {},
  defaultBounds = {};

function isFilterApplied() {
  let filterApplied = false;
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    if (key !== 'region' && value !== undefined && value !== '') {
      filterApplied = true;
    }
  });
  return filterApplied;
}

let regionInteger = null;

if (region && !isFilterApplied()) {
  regionInteger = parseInt(region);
  if (MAP_OPTIONS_PER_REGION.hasOwnProperty(regionInteger)) {
    mapOptions = MAP_OPTIONS_PER_REGION[regionInteger];
    mobileMapOptions = MAP_OPTIONS_PER_REGION_MOBILE[regionInteger];
  }
  if (BOUNDS_PER_REGION.hasOwnProperty(regionInteger)) {
    defaultBounds = BOUNDS_PER_REGION[regionInteger];
  }
}

const initMap = () => {
  const mapView = new CatalogueMapView({
    mapOptions,
    mobileMapOptions,
    defaultBounds,
  });
  mapView.create();
  if (mapView.isMobile) {
    const filters = document.querySelector('.filter-bar-wrapper');
    const tiles = document.querySelector('#above-filter-bar-content');
    const navigation = document.querySelector('#navigation');
    const totalHeight =
      filters.offsetHeight + tiles.offsetHeight + navigation.offsetHeight;
    const map = document.querySelector('#map-mobile');
    map.style.height = `calc(100dvh - ${totalHeight}px)`;
    document.body.style.overflow = 'hidden';
  }
};

window.sales = {
  ...window.sales,
  listView: {
    initMap,
  },
};
